import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { useConfig } from '../context/ConfigContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  RefreshCw, Target, Loader2, Plus, FileText, Save, Wallet, 
  CreditCard, Receipt, AlertCircle, PiggyBank, ArrowUpRight, ArrowDownRight,
  Trash2, List, ToggleLeft, Calendar, Brain, Sparkles, Eye, ChevronRight,
  CalendarClock
} from 'lucide-react';
import { 
  PageHeader, MetricCard, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

// AI Insights Section Component for Financial Health
const AIInsightsSection = ({ domain, data }) => {
  const { token } = useAuth();
  const [insights, setInsights] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/qapi-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ domain, data })
      });
      
      if (res.ok) {
        const result = await res.json();
        setInsights(result);
        toast.success('AI insights generated');
      } else {
        // Generate mock insights for demo
        setInsights({
          summary: `Based on your financial health metrics, overall performance shows positive revenue trends with some areas requiring cost management attention.`,
          key_findings: [
            { type: 'positive', text: 'Revenue is tracking above target by 5.2%' },
            { type: 'positive', text: 'Days in AR has improved compared to last quarter' },
            { type: 'warning', text: 'Pharmacy costs are trending 8% above budget - recommend supplier review' },
            { type: 'action', text: 'ADR rate showing slight increase - consider documentation training' }
          ],
          recommendations: [
            'Negotiate volume discounts with top 3 pharmacy vendors',
            'Implement ADR documentation improvement program',
            'Review bad debt collection processes for accounts >90 days',
            'Consider capitated contracts for frequently used supplies'
          ],
          predicted_trends: {
            next_month: 'stable',
            risk_areas: ['Pharmacy Costs', 'ADR Rate']
          },
          confidence_score: 89
        });
        toast.success('AI insights generated');
      }
    } catch (error) {
      toast.error('Error generating insights');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="border-2 border-[#00b0ab]/20 bg-gradient-to-br from-[#00b0ab]/5 to-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#00b0ab]" />
          AI Insights - Financial Health
        </CardTitle>
        <CardDescription>
          AI-powered analysis and recommendations based on your financial data
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!insights ? (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 mx-auto text-[#00b0ab]/50 mb-4" />
            <p className="text-muted-foreground mb-4">Generate AI-powered insights from your financial metrics</p>
            <Button onClick={generateInsights} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="h-4 w-4 mr-2" />
                  Generate Insights
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="p-4 bg-[#00b0ab]/10 rounded-lg">
              <p className="text-sm font-medium">{insights.summary}</p>
            </div>
            
            {/* Key Findings */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4" /> Key Findings
              </h4>
              <div className="space-y-2">
                {insights.key_findings?.map((finding, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                    finding.type === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                    finding.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                    'bg-rose-50 border border-rose-200'
                  }`}>
                    {finding.type === 'positive' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                    ) : finding.type === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5" />
                    )}
                    <span className="text-sm">{finding.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Recommendations */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" /> Recommendations
              </h4>
              <ul className="space-y-2">
                {insights.recommendations?.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="h-4 w-4 text-[#00b0ab] mt-0.5 shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Confidence Score */}
            <div className="flex items-center justify-between pt-4 border-t">
              <span className="text-sm text-muted-foreground">AI Confidence</span>
              <div className="flex items-center gap-2">
                <Progress value={insights.confidence_score} className="w-24 h-2" />
                <span className="text-sm font-medium">{insights.confidence_score}%</span>
              </div>
            </div>
            
            <Button variant="outline" size="sm" onClick={generateInsights} disabled={generating}>
              <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Regenerate
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function FinancialHealthPage() {
  const { token } = useAuth();
  const { getFinancialTarget } = useConfig();
  
  // Get configurable targets from context
  const financialTargets = useMemo(() => ({
    revenueTarget: getFinancialTarget('monthly_revenue_target', 200000),
    costPerEpisodeTarget: getFinancialTarget('cost_per_episode_target', 21000),
    netMarginTarget: getFinancialTarget('net_margin_target', 0.12),
    badDebtTarget: getFinancialTarget('bad_debt_target', 0.02)
  }), [getFinancialTarget]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Entries state
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Data view toggle - projected vs actual
  const [dataViewType, setDataViewType] = useState('actual'); // 'projected' or 'actual'
  
  // January baseline
  const currentYear = new Date().getFullYear();
  const januaryBaseline = `${currentYear}-01-01`;
  
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryDataType, setEntryDataType] = useState('actual'); // 'projected' or 'actual'
  const [entryData, setEntryData] = useState({
    revenue_actual: 0,
    revenue_target: financialTargets.revenueTarget,
    billable_patient_days: 0,
    dme_spend: 0,
    dme_patients: 0,
    dme_budget: 0,
    pharmacy_spend: 0,
    pharmacy_budget: 0,
    supply_spend: 0,
    supply_budget: 0,
    claims_submitted: 0,
    adr_received: 0,
    adr_responded: 0,
    adr_denied: 0,
    accounts_receivable: 0,
    bad_debt_writeoff: 0,
    days_in_ar: 0
  });

  useEffect(() => {
    fetchDashboard();
    fetchEntries();
  }, [dataViewType]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/financial/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    setEntriesLoading(true);
    try {
      const params = new URLSearchParams();
      if (dataViewType !== 'all') {
        params.append('data_type', dataViewType);
      }
      const res = await fetch(`${API_URL}/api/financial/entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleSaveEntry = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/financial/entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          date: entryDate, 
          data_type: entryDataType,
          ...entryData 
        })
      });
      if (res.ok) {
        toast.success(`Financial entry saved (${entryDataType})`);
        fetchDashboard();
        fetchEntries();
      } else {
        toast.error('Failed to save entry');
      }
    } catch (error) {
      toast.error('Error saving entry');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (entry) => {
    setDeleteTarget(entry);
    setShowDeleteDialog(true);
  };

  const handleDeleteEntry = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/financial/entries/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Entry deleted: ${deleteTarget.date}`);
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        fetchEntries();
        fetchDashboard();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete entry');
      }
    } catch (error) {
      toast.error('Error deleting entry');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const { metrics, overall_score, pip_triggers, trends } = dashboardData || {};

  return (
    <div className="space-y-6" data-testid="financial-health-page">
      {/* Modern Gradient Header */}
      <PageHeader
        icon={DollarSign}
        title="Financial Health"
        subtitle="Revenue, costs, ADR exposure & financial KPIs"
        gradient="from-emerald-600 via-teal-600 to-cyan-600"
      >
        <div className="flex items-center gap-3">
          {/* Data View Toggle */}
          <div className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-1.5">
            <ToggleLeft className="h-4 w-4 text-white" />
            <Select value={dataViewType} onValueChange={setDataViewType}>
              <SelectTrigger className="w-[120px] h-8 bg-transparent border-0 text-white">
                <SelectValue placeholder="Data Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="actual">Actual</SelectItem>
                <SelectItem value="projected">Projected</SelectItem>
                <SelectItem value="all">All Data</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
            onClick={() => { fetchDashboard(); fetchEntries(); }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </PageHeader>

      {/* Data View Indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant={dataViewType === 'actual' ? 'default' : 'secondary'} className={dataViewType === 'actual' ? 'bg-emerald-500' : ''}>
            {dataViewType === 'actual' ? 'Showing Actual Data' : dataViewType === 'projected' ? 'Showing Projected Data' : 'Showing All Data'}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Baseline: January {currentYear}
          </span>
        </div>
      </div>

      {/* Summary Cards with Modern Design */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          title="Financial Health"
          value={`${overall_score || 0}%`}
          icon={PiggyBank}
          gradient="emerald"
          subtitle="Overall Score"
        />
        <MetricCard
          title="Revenue"
          value={formatCurrency(metrics?.revenue?.actual || 0)}
          icon={Wallet}
          gradient={(metrics?.revenue?.variance_pct || 0) >= 0 ? "emerald" : "rose"}
          subtitle={`${(metrics?.revenue?.variance_pct || 0) >= 0 ? '+' : ''}${metrics?.revenue?.variance_pct || 0}% variance`}
        />
        <MetricCard
          title="Pharmacy & Supplies"
          value={formatCurrency((metrics?.pharmacy?.spend || 0) + (metrics?.supply?.spend || 0))}
          icon={Receipt}
          gradient={(((metrics?.pharmacy?.spend || 0) + (metrics?.supply?.spend || 0)) <= ((metrics?.pharmacy?.budget || 0) + (metrics?.supply?.budget || 0))) ? "cyan" : "amber"}
          subtitle={`Budget: ${formatCurrency((metrics?.pharmacy?.budget || 0) + (metrics?.supply?.budget || 0))}`}
        />
        <MetricCard
          title="ADR Rate"
          value={`${metrics?.adr?.rate || 0}%`}
          icon={FileText}
          gradient={(metrics?.adr?.rate || 0) <= 5 ? "blue" : "rose"}
          subtitle="Target: <5%"
        />
        <MetricCard
          title="Bad Debt"
          value={`${metrics?.bad_debt?.percentage || 0}%`}
          icon={CreditCard}
          gradient={(metrics?.bad_debt?.percentage || 0) <= 3 ? "indigo" : "amber"}
          subtitle={`${metrics?.bad_debt?.days_in_ar || 0} days in AR`}
        />
      </div>

      {/* PIP Triggers Alert */}
      {pip_triggers && pip_triggers.length > 0 && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-500 rounded-xl">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-800 dark:text-amber-200">
                  {pip_triggers.length} Financial Metric{pip_triggers.length > 1 ? 's' : ''} Require Attention
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {pip_triggers[0]?.recommended_action}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modern Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <DollarSign className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="ai-insights" className={modernTabTriggerClass}>
            <Brain className="h-4 w-4 mr-2" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="entries" className={modernTabTriggerClass}>
            <List className="h-4 w-4 mr-2" /> Entries
          </TabsTrigger>
          <TabsTrigger value="trends" className={modernTabTriggerClass}>
            <TrendingUp className="h-4 w-4 mr-2" /> Trends
          </TabsTrigger>
          <TabsTrigger value="entry" className={modernTabTriggerClass}>
            <CalendarClock className="h-4 w-4 mr-2" /> Monthly Entry
          </TabsTrigger>
          <TabsTrigger value="adr" className={modernTabTriggerClass}>
            <FileText className="h-4 w-4 mr-2" /> ADR Details
          </TabsTrigger>
        </TabsList>

        {/* AI Insights Tab */}
        <TabsContent value="ai-insights" className="space-y-4">
          <AIInsightsSection domain="financial_health" data={dashboardData} />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Card */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Wallet} title="Revenue Performance" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-4xl font-bold">{formatCurrency(metrics?.revenue?.actual || 0)}</p>
                      <p className="text-sm text-muted-foreground">of {formatCurrency(metrics?.revenue?.target || 200000)} target</p>
                    </div>
                    <Badge className={
                      metrics?.revenue?.status === 'on_track' ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white' :
                      metrics?.revenue?.status === 'monitor' ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-black' :
                      'bg-gradient-to-r from-rose-500 to-red-600 text-white'
                    }>
                      {metrics?.revenue?.status?.replace('_', ' ')?.toUpperCase()}
                    </Badge>
                  </div>
                  <Progress value={Math.min(100, (metrics?.revenue?.actual || 0) / (metrics?.revenue?.target || 1) * 100)} className="h-3" />
                  <p className="text-sm text-muted-foreground">{metrics?.revenue?.billable_days || 0} billable patient days</p>
                </div>
              </CardContent>
            </ModernCard>

            {/* Pharmacy & Supplies Card */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Receipt} title="Pharmacy & Supplies" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                    <span className="text-muted-foreground">Pharmacy Spend</span>
                    <span className="font-bold">{formatCurrency(metrics?.pharmacy?.spend || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                    <span className="text-muted-foreground">Pharmacy Budget</span>
                    <span className="font-medium">{formatCurrency(metrics?.pharmacy?.budget || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Supply Spend</span>
                    <span className="font-bold">{formatCurrency(metrics?.supply?.spend || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Supply Budget</span>
                    <span className="font-medium">{formatCurrency(metrics?.supply?.budget || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* DME Card */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={CreditCard} title="DME Spending" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Total Spend</span>
                    <span className="font-bold">{formatCurrency(metrics?.dme?.spend || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Budget</span>
                    <span className="font-medium">{formatCurrency(metrics?.dme?.budget || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Per Patient</span>
                    <span className="font-medium">{formatCurrency(metrics?.dme?.per_patient || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Variance</span>
                    <span className={`font-bold ${(metrics?.dme?.variance_pct || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {(metrics?.dme?.variance_pct || 0) > 0 ? '+' : ''}{metrics?.dme?.variance_pct || 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </ModernCard>

            {/* AR & Bad Debt Card */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={AlertCircle} title="Accounts Receivable" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">AR Balance</span>
                    <span className="font-bold">{formatCurrency(metrics?.bad_debt?.ar_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Days in AR</span>
                    <span className="font-medium">{metrics?.bad_debt?.days_in_ar || 0} days</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Bad Debt Write-off</span>
                    <span className="font-bold text-red-600">{formatCurrency(metrics?.bad_debt?.writeoff || 0)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <span className="text-muted-foreground">Bad Debt %</span>
                    <span className={`font-bold ${(metrics?.bad_debt?.percentage || 0) > 3 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {metrics?.bad_debt?.percentage || 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </ModernCard>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          {trends && trends.length > 0 ? (
            <>
              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={TrendingUp} title="Revenue Trends" />
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                        <YAxis tickFormatter={(v) => `$${v/1000}k`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} name="Revenue" dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="revenue_target" stroke="#10b981" strokeDasharray="5 5" strokeWidth={2} name="Revenue Target" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </ModernCard>

              <ModernCard>
                <CardHeader>
                  <SectionHeader icon={Receipt} title="Pharmacy & Supplies Trend" />
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                        <YAxis tickFormatter={(v) => `$${v/1000}k`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        <Bar dataKey="pharmacy_spend" fill="#06b6d4" name="Pharmacy Spend" stackId="spend" />
                        <Bar dataKey="supply_spend" fill="#0284c7" name="Supply Spend" stackId="spend" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                      <p className="text-sm text-cyan-700">Total Pharmacy Spend (Period)</p>
                      <p className="text-xl font-bold text-cyan-800">
                        {formatCurrency(trends.reduce((sum, t) => sum + (t.pharmacy_spend || 0), 0))}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">Total Supply Spend (Period)</p>
                      <p className="text-xl font-bold text-blue-800">
                        {formatCurrency(trends.reduce((sum, t) => sum + (t.supply_spend || 0), 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
            </>
          ) : (
            <ModernCard>
              <CardContent className="p-12 text-center">
                <TrendingUp className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold">No trend data available</h3>
                <p className="text-muted-foreground mt-2">Add financial entries to see trends over time</p>
              </CardContent>
            </ModernCard>
          )}
        </TabsContent>

        {/* Data Entry Tab */}
        <TabsContent value="entry">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-[#00b0ab]" />
                Add Financial Entry
              </CardTitle>
              <CardDescription>Enter financial data. Use January as your baseline for data collection.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Label className="w-24">Entry Date</Label>
                  <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="w-48" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="w-24">Data Type</Label>
                  <Select value={entryDataType} onValueChange={setEntryDataType}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actual">Actual</SelectItem>
                      <SelectItem value="projected">Projected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Badge variant={entryDataType === 'actual' ? 'default' : 'outline'} className={entryDataType === 'actual' ? 'bg-emerald-500' : 'bg-blue-100 text-blue-700'}>
                  {entryDataType === 'actual' ? 'Recording Actual Data' : 'Recording Projected Data'}
                </Badge>
              </div>

              {/* Revenue Section */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h3 className="font-semibold text-emerald-700 mb-3">Revenue</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Revenue Actual ($)</Label><Input type="number" value={entryData.revenue_actual} onChange={(e) => setEntryData({...entryData, revenue_actual: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Revenue Target ($)</Label><Input type="number" value={entryData.revenue_target} onChange={(e) => setEntryData({...entryData, revenue_target: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Billable Patient Days</Label><Input type="number" value={entryData.billable_patient_days} onChange={(e) => setEntryData({...entryData, billable_patient_days: parseInt(e.target.value) || 0})} /></div>
                </div>
              </div>

              {/* Pharmacy and Supplies Section */}
              <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <h3 className="font-semibold text-cyan-700 mb-3">Pharmacy and Supplies</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Pharmacy Spend ($)</Label><Input type="number" value={entryData.pharmacy_spend} onChange={(e) => setEntryData({...entryData, pharmacy_spend: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Pharmacy Budget ($)</Label><Input type="number" value={entryData.pharmacy_budget} onChange={(e) => setEntryData({...entryData, pharmacy_budget: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Supply Spend ($)</Label><Input type="number" value={entryData.supply_spend} onChange={(e) => setEntryData({...entryData, supply_spend: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Supply Budget ($)</Label><Input type="number" value={entryData.supply_budget} onChange={(e) => setEntryData({...entryData, supply_budget: parseFloat(e.target.value) || 0})} /></div>
                </div>
              </div>

              {/* DME Section */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-700 mb-3">DME</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>DME Spend ($)</Label><Input type="number" value={entryData.dme_spend} onChange={(e) => setEntryData({...entryData, dme_spend: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>DME Patients</Label><Input type="number" value={entryData.dme_patients} onChange={(e) => setEntryData({...entryData, dme_patients: parseInt(e.target.value) || 0})} /></div>
                  <div><Label>DME Budget ($)</Label><Input type="number" value={entryData.dme_budget} onChange={(e) => setEntryData({...entryData, dme_budget: parseFloat(e.target.value) || 0})} /></div>
                </div>
              </div>

              {/* ADR Section */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-700 mb-3">ADR (Additional Documentation Requests)</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div><Label>Claims Submitted</Label><Input type="number" value={entryData.claims_submitted} onChange={(e) => setEntryData({...entryData, claims_submitted: parseInt(e.target.value) || 0})} /></div>
                  <div><Label>ADRs Received</Label><Input type="number" value={entryData.adr_received} onChange={(e) => setEntryData({...entryData, adr_received: parseInt(e.target.value) || 0})} /></div>
                  <div><Label>ADRs Responded</Label><Input type="number" value={entryData.adr_responded} onChange={(e) => setEntryData({...entryData, adr_responded: parseInt(e.target.value) || 0})} /></div>
                  <div><Label>ADRs Denied</Label><Input type="number" value={entryData.adr_denied} onChange={(e) => setEntryData({...entryData, adr_denied: parseInt(e.target.value) || 0})} /></div>
                </div>
              </div>

              {/* AR Section */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-700 mb-3">Accounts Receivable</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>AR Balance ($)</Label><Input type="number" value={entryData.accounts_receivable} onChange={(e) => setEntryData({...entryData, accounts_receivable: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Bad Debt Write-off ($)</Label><Input type="number" value={entryData.bad_debt_writeoff} onChange={(e) => setEntryData({...entryData, bad_debt_writeoff: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Days in AR</Label><Input type="number" value={entryData.days_in_ar} onChange={(e) => setEntryData({...entryData, days_in_ar: parseFloat(e.target.value) || 0})} /></div>
                </div>
              </div>

              <Button onClick={handleSaveEntry} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Entry ({entryDataType})
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entries Tab - Show all financial entries with delete option */}
        <TabsContent value="entries">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <List className="h-5 w-5 text-[#00b0ab]" />
                Financial Health Entries
              </CardTitle>
              <CardDescription>
                View and manage all financial entries. Currently showing: {dataViewType === 'all' ? 'All entries' : `${dataViewType} entries`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entriesLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No Financial Entries</h3>
                  <p className="text-muted-foreground mt-1">Add your first entry using the Data Entry tab</p>
                  <Button className="mt-4" onClick={() => setActiveTab('entry')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Entry
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Pharmacy & Supplies</TableHead>
                      <TableHead className="text-right">AR Balance</TableHead>
                      <TableHead className="text-right">ADR Rate</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry, idx) => (
                      <TableRow key={entry.id || idx}>
                        <TableCell className="font-medium">
                          {entry.date || (entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.data_type === 'actual' ? 'default' : 'outline'} 
                                 className={entry.data_type === 'actual' ? 'bg-emerald-500' : 'bg-blue-100 text-blue-700'}>
                            {entry.data_type || 'actual'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          ${(entry.revenue_actual || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          ${((entry.pharmacy_spend || 0) + (entry.supply_spend || 0)).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          ${(entry.accounts_receivable || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.claims_submitted > 0 
                            ? `${((entry.adr_received / entry.claims_submitted) * 100).toFixed(1)}%`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => confirmDelete(entry)}
                            data-testid={`delete-entry-${entry.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ADR Details Tab */}
        <TabsContent value="adr">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-red-500" />
                ADR Performance
              </CardTitle>
              <CardDescription>Additional Documentation Requests from payers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-3xl font-bold">{metrics?.adr?.claims_submitted || 0}</p>
                  <p className="text-sm text-slate-500">Claims Submitted</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">{metrics?.adr?.received || 0}</p>
                  <p className="text-sm text-slate-500">ADRs Received</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-emerald-600">{metrics?.adr?.responded || 0}</p>
                  <p className="text-sm text-slate-500">ADRs Responded</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{metrics?.adr?.denied || 0}</p>
                  <p className="text-sm text-slate-500">ADRs Denied</p>
                </div>
              </div>
              <div className="mt-6 p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">ADR Rate</span>
                  <span className={`text-2xl font-bold ${(metrics?.adr?.rate || 0) <= 5 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {metrics?.adr?.rate || 0}%
                  </span>
                </div>
                <Progress value={Math.min(100, (metrics?.adr?.rate || 0) * 10)} className="h-2" />
                <p className="text-xs text-slate-400 mt-2">Target: &lt;5% of claims</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete Financial Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium">{deleteTarget.date || 'Entry'}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Type: {deleteTarget.data_type || 'actual'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Revenue: ${(deleteTarget.revenue_actual || 0).toLocaleString()}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteEntry}
              disabled={deleting}
              data-testid="confirm-delete-financial-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
